import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Logo } from '../../assets/icons/logo_header.svg';
import authService from '../../features/auth/auth-service';
import { Icons, SvgProps } from '../icons/icons';
import { Menu, MenuList, MenuButton, MenuItem } from '@reach/menu-button';
import React, { useContext } from 'react';
import { TenantContext } from '../../state/tenant-state';
import { CacheService } from '../../services/cache-service';
import { GoldMaster } from '../../references/tenant';

const MenuLink = (props: { isExpanded: boolean, children: (string | JSX.Element)[] | string }): JSX.Element => {
  return <MenuButton>
    <span className="text-light header-link">
      {props.children}
      {props.isExpanded ? <Icons.ArrowUp /> : <Icons.ArrowDown />}
    </span>
  </MenuButton>;
};

export const Header = (): JSX.Element => {
  const navigate = useNavigate();
  const tenantContext = useContext(TenantContext);

  const logOut = (): void => {
    authService.logOut();
    navigate('/', { replace: true });
    window.location.pathname = '/';
  };

  return (
    <div className="header">
      <div className="left-side-links">
        <Link to="/" className="header-logo">
          <Logo />
        </Link>
        {menus.map((menu: Menu): JSX.Element => {
          return <Menu key={menu.title}>
            {({ isExpanded }): JSX.Element => (
              <>
                <MenuLink isExpanded={isExpanded}>
                  <menu.Icon />
                  {menu.title}
                </MenuLink>
                <MenuList>
                  {menu.items.map((item): JSX.Element => {
                    return <MenuItem key={item.title} onSelect={(): void => navigate(item.url)}>{item.title}</MenuItem>;
                  })}
                </MenuList>
              </>
            )}
          </Menu>;
        })}
      </div>
      <div className="right-side-links">
        {authService.isSuperAdmin() && tenantContext.items?.length && (
          <Menu>
            {({ isExpanded }): JSX.Element => (
              <>
                <MenuLink isExpanded={isExpanded}>
                  {CacheService.get('pdc.tenant.name')}
                </MenuLink>
                <MenuList className="tenant-list">
                  {((): JSX.Element => {
                    const tenant = tenantContext.items?.find(tenant => tenant.id === GoldMaster);
                    if (!tenant) {
                      return <></>;
                    }
                    return <MenuItem key="gold.master" onSelect={(): void => tenantContext.setTenant?.(tenant)}>Gold Master</MenuItem>;
                  })()}
                  {tenantContext.items?.filter(tenant => tenant.id !== GoldMaster).map(tenant => {
                    return <MenuItem key={tenant.id} onSelect={(): void => tenantContext.setTenant?.(tenant)}>{tenant.name}</MenuItem>;
                  })}
                </MenuList>
              </>
            )}
          </Menu>
        )}
        <span className="text-light header-link" onClick={logOut}>
          <Icons.Logout />
          Log Out
        </span>
      </div>
    </div>
  );
};

type Menu = {
  Icon: (props: SvgProps) => JSX.Element;
  title: string;
  items: { title: string; url: string; }[];
}

const menus: Menu[] = [
  {
    Icon: Icons.Flag,
    title: 'Internationalization',
    items: [
      { title: 'Languages', url: '/internationalization' },
      { title: 'Static Translation Keys', url: '/internationalization/static-translation-keys' }
    ]
  },
  {
    Icon: Icons.Global,
    title: 'Geo Info',
    items: [
      { title: 'Map Servers', url: '/map-servers' },
      { title: 'Map Services', url: '/map-services' },
      { title: 'Map Layers', url: '/map-layers' },
      { title: 'Layer Groups', url: '/layer-groups' },
      { title: 'Value Formatters', url: '/settings/value-formatters' },
      { title: 'Coordinate Systems', url: '/settings/coordinate-systems' },
    ]
  },
  {
    Icon: Icons.Config,
    title: 'Configuration',
    items: [
      { title: 'Table of Contents', url: '/configurations' },
      { title: 'Capabilities', url: '/settings/capabilities' },
      { title: 'Protocols', url: '/settings/protocols' },
      { title: 'Palettes', url: '/settings/palettes' },
      { title: 'Extents', url: '/settings/extents' },
    ]
  },
  {
    Icon: Icons.Application,
    title: 'Application',
    items: [
      { title: 'Settings', url: '/settings' },
      { title: 'Job Management (Monitoring)', url: '/reports/jobs' },
      { title: 'Hazard Types', url: '/settings/hazard-types' },
      { title: 'Hazard Severities', url: '/settings/hazard-severities' },
      { title: 'Hazard Categories', url: '/settings/hazard-categories' },
      { title: 'Reports', url: '/reports' },
    ]
  }
];
